import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { RotateCcw, RefreshCw, TrendingUp, TrendingDown } from "lucide-react";

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <RotateCcw size={11} /> Pending
      </div>
      <h1 className="font-black text-3xl tracking-tight">Pending Cylinders</h1>
      <p className="text-sm text-gray-500 mt-1">Net cylinders pending return per party (filled given − MT received)</p>
    </div>
  );
}

export function PendingCylindersPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch]   = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/pending/cylinders");
      setRows(r.data?.rows || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPending = rows.reduce((s, r) => s + Math.max(0, r.total_pending), 0);
  const totalExcess  = rows.reduce((s, r) => s + Math.max(0, -r.total_pending), 0);

  const filtered = rows.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="px-6 py-6 max-w-5xl">
      <div className="flex items-start justify-between mb-6">
        <PageHeader />
        <button onClick={load} className="mt-6 p-2 hover:bg-gray-100 rounded-lg border border-gray-200">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-black text-gray-900">{rows.length}</div>
          <div className="text-xs text-gray-400 mt-1">Total Parties</div>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-black text-amber-700">{totalPending.toLocaleString("en-IN")}</div>
          <div className="text-xs text-gray-400 mt-1">Pending (not returned)</div>
        </div>
        <div className="bg-white border border-blue-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-black text-blue-700">{totalExcess.toLocaleString("en-IN")}</div>
          <div className="text-xs text-gray-400 mt-1">Excess Returns</div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search party name…"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 w-full max-w-sm" data-testid="pending-cyl-search" />
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Computing pending cylinders…</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
          <RotateCcw size={28} className="mx-auto mb-3 text-gray-300" />
          <div className="text-sm text-gray-500">No pending cylinders</div>
          <div className="text-xs text-gray-400 mt-1">All cylinders have been returned or no sales recorded</div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="pending-cylinders-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Party</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Type</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Phone</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Net Pending</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <React.Fragment key={row.party_id}>
                    <tr className={`border-b border-gray-50 hover:bg-gray-50/50 ${row.total_pending > 0 ? "bg-amber-50/20" : "bg-blue-50/10"}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setExpanded(expanded === row.party_id ? null : row.party_id)}
                          className="font-semibold text-gray-800 hover:text-blue-600 text-left" data-testid={`party-row-${row.party_id}`}>
                          {row.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full capitalize">{row.type}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{row.phone || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold text-base ${row.total_pending > 0 ? "text-amber-700" : row.total_pending < 0 ? "text-blue-700" : "text-gray-400"}`}>
                          {row.total_pending > 0
                            ? <span className="flex items-center justify-end gap-1"><TrendingUp size={14} /> {row.total_pending}</span>
                            : row.total_pending < 0
                            ? <span className="flex items-center justify-end gap-1"><TrendingDown size={14} /> {Math.abs(row.total_pending)} excess</span>
                            : "0"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {(row.breakdown || []).filter(b => b.pending !== 0).slice(0, 4).map((b, j) => (
                            <span key={j} className={`text-xs px-2 py-0.5 rounded-lg border font-mono font-semibold
                              ${b.pending > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                              {b.brand} {b.size}: {b.pending > 0 ? `+${b.pending}` : b.pending}
                            </span>
                          ))}
                          {row.breakdown.filter(b => b.pending !== 0).length > 4 && (
                            <span className="text-xs text-gray-400">+{row.breakdown.filter(b => b.pending !== 0).length - 4} more</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === row.party_id && (
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-6 py-3">
                          <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Full Breakdown</div>
                          <div className="flex flex-wrap gap-2">
                            {(row.breakdown || []).map((b, j) => (
                              <div key={j} className={`border rounded-lg px-3 py-2 text-center min-w-[90px]
                                ${b.pending > 0 ? "bg-amber-50 border-amber-200" : b.pending < 0 ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"}`}>
                                <div className={`text-lg font-black ${b.pending > 0 ? "text-amber-700" : b.pending < 0 ? "text-blue-700" : "text-gray-400"}`}>
                                  {b.pending > 0 ? `+${b.pending}` : b.pending}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">{b.brand} {b.size}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingCylindersPage;
